import React, { useRef, useState, useEffect } from 'react';
import '../Css/Order.css';
import { Link, useLocation } from 'react-router-dom';
import { TbFileTypePdf } from "react-icons/tb";
import Navbar from './Navbar';
import QRCode from "qrcode";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Tooltip } from 'react-tooltip';
import { MdDownloadForOffline } from "react-icons/md";
import { toast } from 'react-toastify';
import { SiConvertio } from "react-icons/si";
import { IoCreateOutline } from "react-icons/io5";
import { MdOutlineUploadFile } from "react-icons/md";
import { CircularProgress, Typography, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, InputAdornment, Autocomplete, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Logger from './Logger';
import { format } from 'date-fns';
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';
import { handlePlanApiError, usePlan } from '../context/PlanContext';
import '../Css/UploadResultDialog.css';

function notifyOrdersChanged() {
  window.dispatchEvent(new CustomEvent('zordigo-orders-updated'));
}

function isPlanLimitPayload(data) {
  return (
    data?.code === 'PLAN_LIMIT' ||
    data?.code === 'USAGE_BLOCKED' ||
    data?.code === 'FEATURE_LOCKED'
  );
}

function Order({ searchQuery, setSearchQuery, selectedOption, setSelectedOption, startDate, setStartDate, endDate, setEndDate }) {
  const userEmail = sessionStorage.getItem('userEmail');
  const { openUpgradeModal } = usePlan();
  const fileInputRef = useRef(null);
  const fileInputRefMarg = useRef(null);
  const fileInputRefERP = useRef(null);
  const [invalidExcelLink, setInvalidExcelLink] = useState('');
  const [margExcelLink, setMargExcelLink] = useState('');
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [options, setOptions] = useState([]); // Store all options fetched from the backend
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [validCount, setValidCount] = useState(0);
  const [invalidCount, setInvalidCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false); // State to control the error modal
  const [pendingUpgradePayload, setPendingUpgradePayload] = useState(null);
  const [areaOptions, setAreaOptions] = useState([]);
  const [salesmanOptions, setSalesmanOptions] = useState([]);
  const [newOrderDetails, setNewOrderDetails] = useState({
    orderId: '',
    PName: '',
    PPhone: '',
    PAddress: '',
    DName: '',
    DPhone: '',
    DEmail: '',
    DAddress: '',
    DDate: '',
    DTime: '',
    INumber: '',
    IDate: '',
    IAmount: '',
    OTime: '',
    PDate: '',
    PTime: '',
    route: '',
    area: '',
    salesman: '',
    summary: '',
    adminEmail: userEmail
  });

  const role = sessionStorage.getItem("role");

  // Initialize Logger
  const logger = useRef(new Logger(getLoggerBaseURL())).current;

  const permissionRef = useRef({});
  const permissionString = sessionStorage.getItem("permissions");

  if (permissionString) {
    permissionRef.current = JSON.parse(permissionString);
  } else {
    logger.log('Permission missing', 'ERROR');
  }

  const permission = permissionRef.current;

  const pickupName = sessionStorage.getItem('companyName');
  const pickupAddress = sessionStorage.getItem('address');
  const pickupPhone = sessionStorage.getItem('phoneNo');
  useEffect(() => {
    const pickDate = sessionStorage.getItem('pickDate');
    const pickTime = sessionStorage.getItem('pickTime');
    const delDate = sessionStorage.getItem('delDate');
    const delTime = sessionStorage.getItem('delTime');
    setNewOrderDetails((prevDetails) => ({
      ...prevDetails,
      ...(pickDate && { PDate: pickDate }),
      ...(pickTime && { PTime: pickTime }),
      ...(delDate && { DDate: delDate }),
      ...(delTime && { DTime: delTime }),
    }));
  }, []);

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const queuePlanUpgrade = (data) => {
    if (!isPlanLimitPayload(data)) return false;
    setPendingUpgradePayload(data);
    return true;
  };

  const handleModalClose = () => {
    setIsErrorModalOpen(false);
    const upgradePayload = pendingUpgradePayload;
    setPendingUpgradePayload(null);
    // Only refresh when something was actually saved; otherwise keep the page
    // so the user can read / download errors.
    if (validCount > 0 || updatedCount > 0) {
      notifyOrdersChanged();
      window.location.reload();
      return;
    }
    // Open upgrade after upload dialog closes so they don't stack
    if (upgradePayload) {
      setTimeout(() => openUpgradeModal(upgradePayload), 180);
    }
  };

  const downloadErrorMessages = () => {
    const text =
      typeof uploadError === 'string'
        ? uploadError
        : Array.isArray(uploadError)
          ? uploadError
            .map((e) =>
              typeof e === 'string'
                ? e
                : `Order ${e?.orderNo || '—'}: ${(e?.errors || []).join(', ')}`
            )
            .join('\n')
          : String(uploadError || 'No error details');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'erp_upload_errors.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    try {
      // Fetch order details from the backend
      const response = await fetch(`/api/ordera/todayOrders/${userEmail}`);
      if (!response.ok) {
        throw new Error("Failed to fetch order details");
      }
      const orderDetails = await response.json();
      // Initialize jsPDF
      const doc = new jsPDF();
      // Add page border
      doc.setLineWidth(0.5);
      doc.rect(10, 10, 190, 277); // x, y, width, height
      // Add centered heading
      doc.setFontSize(16);
      doc.text("ZORDIGO", 105, 20, { align: "center" }); // Centered text
      // Add subheading below the main heading
      doc.setFontSize(14);
      doc.text("Order Details", 105, 30, { align: "center" }); // Centered text
      // Add today's date in the top-left corner
      const todayDate = new Date().toLocaleDateString();
      doc.setFontSize(12);
      doc.text(`Date: ${todayDate}`, 14, 20); // Left-aligned text
      // Group orders by area
      const groupedOrders = orderDetails.reduce((groups, order) => {
        const area = order.area || "Unknown Area"; // Replace 'Rec_Add' with your area field
        if (!groups[area]) groups[area] = [];
        groups[area].push(order);
        return groups;
      }, {});

      const tableColumnHeaders = ["Order ID", "Customer Name", "Amount", "QR Code"];
      const tableRows = [];

      for (const [area, orders] of Object.entries(groupedOrders)) {
        // Add a row for the group heading (area name)
        tableRows.push([{ content: `Area: ${area}`, colSpan: 4, styles: { halign: "left", fontStyle: "bold" } }]);

        // Add orders under this area
        for (const order of orders) {
          // Convert order details to a string for QR code
          const orderDetailsString = JSON.stringify({
            orderid: order._id,
            orderNo: order.Order_No,
            compName: order.Comp_Name,
            recName: order.Rec_Name,
            recAdd: order.Rec_Add,
            amount: order.amount,
            distance: order.distance,
            oTime: order.o_time,
            pickTime: order.Pick_Time,
            delTime: order.Del_Time,
            adminEmail: userEmail,
          });

          // Generate QR code as a base64 image
          const qrCodeDataUrl = await QRCode.toDataURL(orderDetailsString);

          // Add the order row
          tableRows.push([
            order.Order_No,
            order.Rec_Name,
            `${order.amount}`,
            { qr: qrCodeDataUrl }, // Custom object for QR code
          ]);
        }
      }

      // Use `autoTable` to add the table
      doc.autoTable({
        head: [tableColumnHeaders],
        body: tableRows,
        startY: 40, // Adjust starting position below heading
        rowPageBreak: "auto",
        styles: { cellPadding: 5, minCellHeight: 20 },
        didDrawCell: (data) => {
          // Add QR codes in the respective cells
          if (data.column.index === 3 && data.cell.raw.qr) {
            doc.addImage(data.cell.raw.qr, "PNG", data.cell.x + 2, data.cell.y + 2, 15, 15); // Adjust dimensions and positioning
          }
        },
      });

      // Save the PDF
      doc.save("Order_Details.pdf");
    } catch (error) {
      logger.log(`Error generating PDF: ${error.message}`, 'ERROR');
      alert("Failed to generate the PDF. Please try again.");
    }
  };

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch(`/api/ledger/fetchLedgers/${userEmail}`);
        const data = await response.json();

        setOptions(data || []);
        setFilteredOptions(data || []);
      } catch (error) {
        logger.log(`Error fetching options: ${error.message}`, 'ERROR');
      }
    };

    fetchOptions();
  }, [userEmail, logger]);

  const handleFileUploadMarg = async (event) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    setLoading(true); // Start loading
    try {
      const response = await fetch(`/api/ordera/api/upload-marg/${userEmail}`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Marg Data Uploaded successfully');
        setMargExcelLink(data.downloadUrl); // URL to download the processed file
        setOpenDialog(true); // Open the dialog box
      } else {
        let errorMessage = 'Some rows were not saved due to validation errors:\n';
        if (Array.isArray(data.errors)) {
          errorMessage += data.errors
            .map(errorObj => `Order No: ${errorObj.orderNo}\nErrors: ${errorObj.errors.join(', ')}`)
            .join('\n\n');
        } else {
          errorMessage = 'Incorrect Format';
        }
        setUploadError(errorMessage);
        handleOpenErrorModal(); // Open the error modal
      }
    } catch (error) {
      setUploadError(error.message); // Set the upload error
      handleOpenErrorModal(); // Open the error modal
    } finally {
      setLoading(false); // End loading
    }
  };

  const handleFileUploadERP = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      toast.error('Please select an Excel file to upload.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);
    setUploadError('');
    setInvalidExcelLink('');
    setValidCount(0);
    setInvalidCount(0);
    setUpdatedCount(0);

    const formatUploadErrors = (data) => {
      if (Array.isArray(data?.errors) && data.errors.length) {
        return data.errors
          .map((errorObj) => {
            if (typeof errorObj === 'string') return errorObj;
            const orderNo = errorObj?.orderNo ?? '—';
            const errs = Array.isArray(errorObj?.errors)
              ? errorObj.errors.join(', ')
              : String(errorObj?.errors || errorObj?.message || errorObj || 'Unknown error');
            return `Order No: ${orderNo}\nErrors: ${errs}`;
          })
          .join('\n\n');
      }
      return data?.details || data?.message || data?.error || 'Incorrect Format';
    };

    const showFailure = (data, fallbackMessage) => {
      queuePlanUpgrade(data);
      const errorMessage = formatUploadErrors(data) || fallbackMessage;
      setUploadError(errorMessage);
      setValidCount(data?.vCount ?? 0);
      setInvalidCount(data?.iCount ?? 0);
      setUpdatedCount(data?.uCount ?? 0);
      setInvalidExcelLink(data?.downloadUrl || '');
      toast.error(data?.message || fallbackMessage, { autoClose: 5000 });
      handleOpenErrorModal();
    };

    try {
      const response = await fetch(`/api/ordera/api/ERP/${userEmail}`, {
        method: 'POST',
        body: formData,
      });
      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        data = {};
      }

      const created = Number(data.vCount) || 0;
      const updated = Number(data.uCount) || 0;
      const failed = Number(data.iCount) || 0;
      const hasErrors = Array.isArray(data.errors) && data.errors.length > 0;

      queuePlanUpgrade(data);

      if (response.ok && (created > 0 || updated > 0) && failed === 0 && !hasErrors) {
        toast.success(
          `ERP upload done — ${created} new` +
          (updated ? `, ${updated} updated` : '') +
          '.',
          { autoClose: 3500 }
        );
        notifyOrdersChanged();
        setTimeout(() => window.location.reload(), 1200);
      } else if (response.ok && (created > 0 || updated > 0)) {
        setValidCount(created);
        setUpdatedCount(updated);
        setInvalidCount(failed);
        setUploadError(formatUploadErrors(data));
        setInvalidExcelLink(data.downloadUrl || '');
        toast.warning(
          `${created} new, ${updated} updated, ${failed} skipped. See details.`,
          { autoClose: 6000 }
        );
        notifyOrdersChanged();
        handleOpenErrorModal();
      } else if (response.ok && created === 0 && updated === 0) {
        // Backend returned 200 but nothing was saved — treat as failure
        showFailure(
          {
            ...data,
            message:
              data.message ||
              '0 orders uploaded. Check column mappings, start row, and ledger names.',
            errors: data.errors?.length
              ? data.errors
              : [
                {
                  orderNo: '—',
                  errors: [
                    'No orders were created or updated. Verify Settings → Order Upload Settings (column letters + start row) and that company names exist in Ledgers.',
                  ],
                },
              ],
            vCount: 0,
            iCount: data.iCount || 0,
          },
          '0 orders uploaded'
        );
      } else {
        showFailure(data, data.message || 'ERP Excel upload failed');
      }
      logger.log(`Uploaded ERP file response: ${JSON.stringify(data)}`, 'INFO');
    } catch (error) {
      const networkMsg =
        error.message === 'Failed to fetch' || /timed out/i.test(error.message || '')
          ? `${error.message}. The server may still have saved some orders — refresh the Orders page to check, then re-upload only missing rows.`
          : error.message || 'ERP Excel upload failed';
      showFailure(
        { errors: [{ orderNo: '—', errors: [networkMsg] }] },
        networkMsg
      );
      notifyOrdersChanged();
    } finally {
      setLoading(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    setLoading(true); // Start loading
    try {
      const response = await fetch(`/api/ordera/api/upload/${userEmail}`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      queuePlanUpgrade(data);
      if (response.ok) {
        toast.success(data.message || 'Data Uploaded successfully');
        notifyOrdersChanged();
        window.location.reload();
      } else {
        // Format the errors for display
        let errorMessage = 'Some rows were not saved due to validation errors:\n';
        if (Array.isArray(data.errors)) {
          errorMessage += data.errors.map(errorObj => `Order No: ${errorObj.orderNo}\nErrors: ${errorObj.errors.join(', ')}`).join('\n\n');
        } else {
          errorMessage = data.message || 'Incorrect Format';
        }
        setUploadError(errorMessage);
        setValidCount(data.vCount || 0);
        setInvalidCount(data.iCount || 0);
        setInvalidExcelLink(data.downloadUrl);
        if ((data.vCount || 0) > 0) notifyOrdersChanged();
        handleOpenErrorModal(); // Open the error modal
      }
      logger.log(`Uploaded file data: ${data}`, 'INFO');
    } catch (error) {
      const networkMsg =
        error.message === 'Failed to fetch' || /timed out/i.test(error.message || '')
          ? `${error.message}. The server may still have saved some orders — refresh the Orders page to check.`
          : error.message;
      setUploadError(networkMsg);
      notifyOrdersChanged();
      handleOpenErrorModal();
    } finally {
      setLoading(false);
      if (event.target) event.target.value = '';
    }
  };

  useEffect(() => {
    if ((permission?.setPickup ?? true) && pickupName) {
      setNewOrderDetails((prevDetails) => ({
        ...prevDetails,
        PName: pickupName,
        PAddress: pickupAddress,
        PPhone: pickupPhone
      }));
    }
  }, [permission, pickupName, pickupAddress, pickupPhone]);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleButtonClickERP = () => {
    fileInputRefERP.current.click();
  };

  const handleDropdownChange = (event) => {
    const next = event.target.value;
    setSelectedOption(next);
    setSearchQuery('');
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!userEmail) return;
      try {
        const res = await fetch(`/api/ordera/filterOptions/${userEmail}`);
        if (!res.ok) return;
        const data = await res.json();
        setAreaOptions(Array.isArray(data.areas) ? data.areas : []);
        setSalesmanOptions(Array.isArray(data.salesmen) ? data.salesmen : []);
      } catch (error) {
        logger.log(`Error fetching filter options: ${error.message}`, 'ERROR');
      }
    };
    fetchFilterOptions();
  }, [userEmail, logger]);

  function downloadDemoExcel() {
    const link = document.createElement('a');
    link.href = "/assets/Order.xlsx";
    link.download = "sample.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setValidationErrors({});
  };

  const handleOpenErrorModal = () => {
    setIsErrorModalOpen(true);
  };

  const handleInputChange = (field, value) => {
    if ((field === 'PPhone' || field === 'DPhone') && (value.length > 10 || isNaN(value))) {
      return;
    }
    if (field === "INumber") {
      // Allow only letters (A-Z, a-z), numbers (0-9), hyphen (-), and dot (.)
      value = value.replace(/[^a-zA-Z0-9-.]/g, '');
    }
    setNewOrderDetails((prevDetails) => ({
      ...prevDetails,
      [field]: value,
    }));
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [field]: '',
    }));
  };

  const handleSaveOrder = async () => {
    const errors = {};
    // Validation checks for order details
    if (!newOrderDetails.orderId) {
      errors.orderId = 'Order ID is required';
    }
    if (!newOrderDetails.PName) {
      errors.PName = 'Customer Name is required';
    }
    if (!newOrderDetails.PPhone || newOrderDetails.PPhone.length !== 10) {
      errors.PPhone = 'Pick Phone must be 10 digits';
    }
    if (!newOrderDetails.PAddress) {
      errors.PAddress = 'Pick Address is required';
    }
    if (!newOrderDetails.PDate) {
      errors.PDate = 'Pick Date is required';
    }
    if (!newOrderDetails.PTime) {
      errors.PTime = 'Pick Time is required';
    }
    if (!newOrderDetails.DName) {
      errors.DName = 'Delivery Name is required';
    }
    if (!newOrderDetails.DPhone || newOrderDetails.DPhone.toString().length !== 10) {
      errors.DPhone = 'Delivery Phone must be 10 digits';
    }
    if (!newOrderDetails.DAddress) {
      errors.DAddress = 'Delivery Address is required';
    }
    if (!newOrderDetails.DDate) {
      errors.DDate = 'Delivery Date is required';
    }
    if (!newOrderDetails.DTime) {
      errors.DTime = 'Delivery Time is required';
    }
    if (!newOrderDetails.INumber) {
      errors.INumber = 'Invoice Number is required';
    }
    if (!newOrderDetails.IAmount) {
      errors.IAmount = 'Invoice Amount is required';
    }
    if (!String(newOrderDetails.IDate || "").trim()) {
      errors.IDate = 'Invoice Date is required';
    }
    if (!newOrderDetails.route) {
      errors.route = 'Route is required';
    }
    if (!newOrderDetails.area) {
      errors.area = 'Area is required';
    }
    // If validation errors exist, set them and return early
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    try {
      // Log the new order details
      logger.log(`new data: ${newOrderDetails}`, 'INFO');
      // First save to the database
      const response = await fetch(`/api/ordera/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newOrderDetails),
      });

      if (response.ok) {
        toast.success("Order Added Successfully");
        notifyOrdersChanged();
        // Reload the page after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else if (response.status === 400) {
        const responseData = await response.json();
        toast.error(responseData.message || "Invalid Address");
      } else if (response.status === 401) {
        const responseData = await response.json();
        toast.error(responseData.message || "Order Number Already Exist");
      } else if (response.status === 402) {
        const responseData = await response.json();
        if (handlePlanApiError(responseData, openUpgradeModal)) {
          toast.error(responseData.message || "Account locked");
        } else {
          toast.error(responseData.message || "Order Created but not auto assigned");
          notifyOrdersChanged();
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      } else if (response.status === 403) {
        const responseData = await response.json();
        handlePlanApiError(responseData, openUpgradeModal);
        toast.error(responseData.message || "Order limit reached for your plan");
      } else {
        const responseData = await response.json();
        if (handlePlanApiError(responseData, openUpgradeModal)) {
          toast.error(responseData.message || "Plan limit");
        } else if (responseData.message === "Invalid element in Distance Matrix API response") {
          toast.error("There should be valid distance between Pickup Address and Delivery Address");
        } else {
          toast.error(responseData.message || "Failed to add Order");
        }
      }
    } catch (error) {
      toast.error(`Error adding order: ${error.message}`);
    }
    setIsModalOpen(false); // Close the modal
  };

  return (
    <div className="orderBody">
      <Navbar />
      <div className="containerD">
        <div className='HeaderD'>
          <p className='Dp'>Orders</p>
        </div>
        <div className='SearchD'>
          <div className="search-container">
            {selectedOption === 'Area' ? (
              <select
                className='searchboxD searchSelectD'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              >
                <option value="">Select Area...</option>
                {areaOptions.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            ) : selectedOption === 'Salesman' ? (
              <select
                className='searchboxD searchSelectD'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              >
                <option value="">Select Salesman...</option>
                {salesmanOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                placeholder="Enter Keyword..."
                className='searchboxD'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
              />
            )}
          </div>
        </div>

        {/* Dropdown Menu */}
        <select value={selectedOption} onChange={handleDropdownChange} className="dropdownD">
          <option value="OrderNo">Order No.</option>
          <option value="Driver">Driver Name</option>
          <option value="CName">Customer Name</option>
          <option value="Salesman">Salesman</option>
          <option value="Area">Area</option>
          <option value="Picked">Picked Up Orders</option>
          <option value="Collection">Bills for Collection</option>
          <option value="Unassigned">Unassigned Orders</option>
          <option value="OnWay">On the Way Orders</option>
        </select>

        <div className="date-picker-container">
          <div className="date-picker">
            <input type="date" id="startDate" title="Invoice date from" value={startDate || ''} onChange={handleStartDateChange} autoComplete="off" />
            <input type="date" id="endDate" title="Invoice date to" value={endDate || ''} onChange={handleEndDateChange} autoComplete="off" />
          </div>
        </div>
        <div className='uploadD'>
          <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} ref={fileInputRef} style={{ display: 'none' }} autoComplete="off" />
        </div>
        <div className='uploadD'>
          <input type="file" accept=".xlsx, .xls" onChange={handleFileUploadMarg} ref={fileInputRefMarg} style={{ display: 'none' }} autoComplete="off" />
        </div>
        <div className='uploadD'>
          <input type="file" accept=".xlsx, .xls" onChange={handleFileUploadERP} ref={fileInputRefERP} style={{ display: 'none' }} autoComplete="off" />
        </div>
        <div className='newwwD'>
          {role === 'admin' || (role === 'dispatcher' && permission.billUpload) ? (
            <button className='choose-file-button-order' onClick={handleButtonClick} style={{ background: '#79c6e9', border: 'none', cursor: 'pointer', padding: '6px' }} data-tooltip-id="upload-tooltip">
              <MdOutlineUploadFile size={30} />
            </button>
          ) : null}
          <Tooltip id="upload-tooltip" place="bottom" content="Upload Excel File" />
        </div>
        <div className='newwD'>
          <button className='choose-file-button-order' onClick={downloadDemoExcel} style={{ background: '#79c6e9', border: 'none', cursor: 'pointer', padding: '6px' }} data-tooltip-id="download-tooltip">
            <MdDownloadForOffline size={30} />
          </button>
          <Tooltip id="download-tooltip" place="bottom" content="Download Excel" />
        </div>
        <div className='newwwwwD'>
          {role === 'admin' || (role === 'dispatcher' && permission.billUpload) ? (
            <button className='choose-file-button-order' onClick={handleButtonClickERP} style={{ background: '#79c6e9', border: 'none', cursor: 'pointer', padding: '7px', marginTop: '2px' }} data-tooltip-id="ledgers-tooltip">
              <SiConvertio size={28} />
            </button>
          ) : null}
          <Tooltip id="ledgers-tooltip" place="bottom" content="Upload ERP Excel" />
        </div>
        <div className='newD'>
          {role === 'admin' || (role === 'dispatcher' && permission.billUpload) ? (
            <button className='choose-file-button-order' onClick={handleOpenModal} style={{ background: '#79c6e9', border: 'none', cursor: 'pointer', padding: '4px 4px 7px 7px' }} data-tooltip-id="create-tooltip">
              <IoCreateOutline size={30} />
            </button>
          ) : null}
          <Tooltip id="create-tooltip" place="bottom" content="Create Order" />
        </div>
        <div className='newD'>
          <button className='choose-file-button-order' onClick={handleDownloadPDF} style={{ background: '#79c6e9', border: 'none', cursor: 'pointer', padding: '6px' }} data-tooltip-id="pdf-tooltip">
            <TbFileTypePdf size={30} />
          </button>
          <Tooltip id="pdf-tooltip" place="bottom" content="PDF Download" />
        </div>
      </div>
      <div className='buttonD'>
        <button className={`buttonsD ${location.pathname === '/CurrentOrder' ? 'highlighted' : ''}`}><Link to='/CurrentOrder'>All Orders</Link></button>
        <button className={`buttonsD ${location.pathname === '/AssignedOrder' ? 'highlighted' : ''}`}><Link to='/AssignedOrder'>Assigned</Link></button>
        <button className={`buttonsD ${location.pathname === '/CompleteOrder' ? 'highlighted' : ''}`}><Link to='/CompleteOrder'>Completed</Link></button>
        <button className={`buttonsD ${location.pathname === '/IncompleteOrder' ? 'highlighted' : ''}`}><Link to='/IncompleteOrder'>Incomplete</Link></button>
        {/* <button className={`buttonsD ${location.pathname === '/Pending' ? 'highlighted' : ''}`}><Link to='/Pending'>Pending Amount</Link></button>
        <button className={`buttonsD ${location.pathname === '/BillToCollect' ? 'highlighted' : ''}`}><Link to='/BillToCollect'>Bills To Collect</Link></button> */}
      </div>
      {/* Show CircularProgress while loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', position: 'fixed', top: 0, left: 0, width: '100%', backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 1000 }}>
          <CircularProgress />
          <Typography variant="h6" style={{ marginLeft: '20px' }}>Uploading...</Typography>
        </div>
      )}
      {/* Upload summary / errors */}
      <Dialog
        open={isErrorModalOpen}
        onClose={handleModalClose}
        maxWidth="sm"
        fullWidth
        className="upload-result-root"
        BackdropProps={{ className: 'upload-result-backdrop' }}
        PaperProps={{ className: 'upload-result-paper' }}
      >
        {(() => {
          const isPartialOk = validCount > 0 || updatedCount > 0;
          const detailsText = uploadError
            ? Array.isArray(uploadError)
              ? uploadError
                .map((errorObj) =>
                  typeof errorObj === 'string'
                    ? errorObj
                    : `Order No: ${errorObj?.orderNo ?? '—'}\nErrors: ${(errorObj?.errors || []).join(', ')}`
                )
                .join('\n\n')
              : String(uploadError)
            : 'No detail was returned. Check Settings → Order Upload Settings (column letters and start row), and confirm company names exist in Ledgers.';

          return (
            <>
              <div className="upload-result-header">
                <div className="upload-result-header-main">
                  <div className={`upload-result-icon ${isPartialOk ? 'ok' : 'fail'}`}>
                    {isPartialOk ? '✓' : '!'}
                  </div>
                  <div>
                    <h2 className="upload-result-heading">
                      {isPartialOk ? 'Upload summary' : 'Upload failed'}
                    </h2>
                    <p className="upload-result-subtitle">
                      {isPartialOk
                        ? 'Some rows were saved. Review skipped rows below if needed.'
                        : 'No new orders were saved. See details below.'}
                    </p>
                  </div>
                </div>
                <IconButton
                  className="upload-result-close"
                  onClick={handleModalClose}
                  size="small"
                  aria-label="Close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>

              <DialogContent className="upload-result-body">
                <div className="upload-result-stats">
                  <div className="upload-result-stat">
                    <span className={`upload-result-stat-value ${validCount > 0 ? 'ok' : ''}`}>
                      {validCount}
                    </span>
                    <span className="upload-result-stat-label">New</span>
                  </div>
                  <div className="upload-result-stat">
                    <span className={`upload-result-stat-value ${updatedCount > 0 ? 'warn' : ''}`}>
                      {updatedCount}
                    </span>
                    <span className="upload-result-stat-label">Updated</span>
                  </div>
                  <div className="upload-result-stat">
                    <span className={`upload-result-stat-value ${invalidCount > 0 ? 'bad' : ''}`}>
                      {invalidCount}
                    </span>
                    <span className="upload-result-stat-label">Skipped</span>
                  </div>
                </div>

                <p className="upload-result-note">
                  One failed row does not block others. Skipped rows are listed below.
                </p>

                <pre className={`upload-result-details${isPartialOk ? '' : ' is-error'}`}>
                  {detailsText}
                </pre>
              </DialogContent>

              <DialogActions className="upload-result-actions">
                <Button className="upload-result-btn-muted" onClick={handleModalClose}>
                  Close
                </Button>
                <Button
                  className="upload-result-btn-secondary"
                  onClick={downloadErrorMessages}
                  variant="outlined"
                >
                  Download Errors
                </Button>
                {invalidExcelLink && (
                  <Button
                    className="upload-result-btn-primary"
                    variant="contained"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${invalidExcelLink}`);
                        if (!response.ok) throw new Error('Failed to download file');

                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'invalid_rows.xlsx';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        logger.log(`Download error: ${error.message}`, 'ERROR');
                        toast.error('Failed to download invalid Excel file.');
                      }
                    }}
                  >
                    Download Invalid Excel
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>New Order</DialogTitle>
        <DialogContent>
          <h3 className='headingD'>Order Details</h3>
          <TextField size='small' margin="dense" label="Order ID*" className='inputD' fullWidth value={newOrderDetails.orderId} onChange={(e) => handleInputChange('orderId', e.target.value)} error={!!validationErrors.orderId} helperText={validationErrors.orderId} />
          <h3 className='headingD'>Pickup From:</h3>
          <TextField size='small' margin="dense" label="Company Name*" fullWidth value={newOrderDetails.PName} onChange={(e) => handleInputChange('PName', e.target.value)} error={!!validationErrors.PName} helperText={validationErrors.PName} />
          <TextField margin="dense" label="Phone No.*" type="tel" size='small' fullWidth value={newOrderDetails.PPhone} onChange={(e) => handleInputChange('PPhone', e.target.value)} error={!!validationErrors.PPhone} helperText={validationErrors.PPhone} />
          <TextField margin="dense" label="Address*" fullWidth size='small' value={newOrderDetails.PAddress} onChange={(e) => handleInputChange('PAddress', e.target.value)} error={!!validationErrors.PAddress} helperText={validationErrors.PAddress} />
          <TextField margin="dense" label="Pickup Date*" type="date" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails.PDate} onChange={(e) => handleInputChange('PDate', e.target.value)} error={!!validationErrors.PDate} helperText={validationErrors.PDate} />
          <TextField margin="dense" label="Pickup Time*" type="time" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails.PTime} onChange={(e) => handleInputChange('PTime', e.target.value)} error={!!validationErrors.PTime} helperText={validationErrors.PTime} />
          <h3 className='headingD'>Deliver To:</h3>
          <Autocomplete options={filteredOptions} value={newOrderDetails.DName}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.ledger || '')}
            isOptionEqualToValue={(option, value) => option.ledger === value}
            onInputChange={(event, newInputValue) => {
              if (event && event.type === 'change') {
                // Update the filtered options based on user input
                setFilteredOptions(
                  options.filter((opt) =>
                    opt.ledger.toLowerCase().includes(newInputValue.toLowerCase())
                  )
                );
                // Update the state with user input
                handleInputChange("DName", newInputValue);
              }
            }}
            onChange={(event, newValue) => {
              handleInputChange("DName", newValue ? newValue.ledger : '');
              if (newValue) {
                handleInputChange("DPhone", newValue.phone || ''); // Set phone number based on the selected item
                handleInputChange("DAddress", newValue.address || '');
                handleInputChange("DDate", newValue.del_date || '');
                handleInputChange("DTime", newValue.del_time || '');
                handleInputChange("area", newValue.area || '');
                handleInputChange("route", newValue.route || '');
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Name*" size="small" margin="dense" fullWidth error={!!validationErrors.DName} helperText={validationErrors.DName} />
            )}
          />
          <TextField margin="dense" label="Phone No.*" type="tel" size='small' fullWidth value={newOrderDetails.DPhone} onChange={(e) => handleInputChange('DPhone', e.target.value)} error={!!validationErrors.DPhone} helperText={validationErrors.DPhone} />
          <TextField margin="dense" label="Address*" size='small' fullWidth value={newOrderDetails.DAddress} onChange={(e) => handleInputChange('DAddress', e.target.value)} error={!!validationErrors.DAddress} helperText={validationErrors.DAddress} />
          <TextField margin="dense" label="Delivery Date*" type="date" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails?.DDate ? format(new Date(newOrderDetails.DDate), 'yyyy-MM-dd') : ''} onChange={(e) => handleInputChange('DDate', e.target.value)} error={!!validationErrors.DDate} helperText={validationErrors.DDate} />
          <TextField margin="dense" label="Delivery Time*" type="time" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails.DTime} onChange={(e) => handleInputChange('DTime', e.target.value)} error={!!validationErrors.DTime} helperText={validationErrors.DTime} />
          <TextField margin="dense" label="Area*" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails.area} onChange={(e) => handleInputChange('area', e.target.value)} error={!!validationErrors.area} helperText={validationErrors.area} />
          <TextField margin="dense" label="Route*" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails.route} onChange={(e) => handleInputChange('route', e.target.value)} error={!!validationErrors.route} helperText={validationErrors.route} />
          <TextField margin="dense" label="Salesman" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails.salesman} onChange={(e) => handleInputChange('salesman', e.target.value)} helperText="e.g. F Pravindra 9818976498" />
          <TextField margin="dense" label="Summary" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails.summary} onChange={(e) => handleInputChange('summary', e.target.value)} />
          <TextField margin="dense" label="Order Placing Time dd/mm/yy hh/mm" size='small' fullWidth InputLabelProps={{ shrink: true }} value={newOrderDetails.OTime} onChange={(e) => handleInputChange('OTime', e.target.value)} />
          <h3 className='headingD'>Invoice Details:</h3>
          <TextField margin="dense" label="Invoice Number*" size='small' fullWidth value={newOrderDetails.INumber} onChange={(e) => handleInputChange('INumber', e.target.value)} error={!!validationErrors.INumber} helperText={validationErrors.INumber} />
          <TextField margin="dense" label="Invoice Amount*" size='small' fullWidth value={newOrderDetails.IAmount} onChange={(e) => handleInputChange('IAmount', e.target.value)} error={!!validationErrors.IAmount} helperText={validationErrors.IAmount || 'Enter amount in Indian Rupee (₹)'} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment>, }} />
          <TextField margin="dense" label="Invoice Date*" size='small' type='date' fullWidth value={newOrderDetails.IDate} InputLabelProps={{ shrink: true }} onChange={(e) => handleInputChange('IDate', e.target.value)} error={!!validationErrors.IDate} helperText={validationErrors.IDate} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSaveOrder}>Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Excel Processed Successfully</DialogTitle>
        <DialogContent>
          <Typography>The Excel file has been processed. You can download the converted file below.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">Close</Button>
          <Button
            onClick={async () => {
              try {
                const response = await fetch(`${margExcelLink}`);
                if (!response.ok) throw new Error('Failed to download file');

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.href = url;
                a.download = 'Order_Excel.xlsx';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                logger.log(`Download error: ${error.message}`, 'ERROR');
                toast.error('Failed to download Excel file.');
              }
            }}
            variant="contained"
            color="secondary"
          >
            Download Excel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Order;